/**
 * تطبيقُ GoyOne لسطح المكتب — إن كانت الصفحةُ تعمل داخله.
 *
 * التطبيقُ يضع هذا الجسر قبل تحميل الصفحة (`apps/desktop/src/preload.cjs`).
 * وفي المتصفّح لا جسر، فكلُّ ما يخصّ التطبيق يُتخطّى بصمت.
 *
 * والفرقُ الذي يصنعه: التطبيقُ يجيب طلبَ مشاركة الشاشة بنفسه بعد موافقةٍ
 * واحدةٍ محفوظةٍ على الجهاز، فتبدأ المراقبةُ دون نافذة اختيارٍ ولا ضغطة.
 * وعلى ويندوز يطلب الهاتفَ المقترن بالبلوتوث مباشرة: الاتصالُ من ضغطة
 * «اتصال» وحدها، والإنهاءُ والكتمُ من الشاشة — لا نافذةَ فون لينك بينهما.
 */

/** حالةُ الخطّ والمكالمة كما يراها ويندوز. */
export interface PhoneState {
  line: { name: string; transport: string; canDial: boolean } | null;
  active: boolean;
  incoming: boolean;
  call: {
    id: string;
    /**
     * Dialing | Talking | Held | Ended | … (PhoneCallStatus).
     * والمردودُ عليها «Talking» لا «Active» — قُرئ من خطٍّ قائم.
     */
    status: string;
    muted: boolean;
    audio: string;
  } | null;
  /**
   * نافذةُ مكالمةِ «فون لينك» قائمةٌ الآن.
   *
   * وهي أصدقُ من `call` في معرفةِ أنّ مكالمةً تجري: الخطُّ قد يُبلّغ
   * «Ended» أو لا يُبلّغ شيئًا والمكالمةُ على الشاشة، والنافذةُ تُولد
   * معها وتموت معها من أيِّ بابٍ أُقفلت.
   */
  window?: boolean;
  /**
   * ميكروفونُ الجهازِ مكتوم.
   *
   * ويندوز يرفضُ كتمَ المكالمةِ التي صوتُها على الحاسب، وميكروفونُها هو
   * ميكروفونُ الجهاز — فيُكتم هو، وبه يُعرف أنّها مكتومةٌ حقًّا.
   */
  micMuted?: boolean;
}

export interface PhoneReply {
  ok: boolean;
  error?: string;
  result?: string;
  state?: PhoneState;
}

export type PhoneCommand =
  'status' | 'dial' | 'end' | 'mute' | 'unmute' | 'hold' | 'resume' | 'answer' | 'reject';

export interface GoyoneDesktop {
  isDesktop: true;
  platform: string;
  monitoringChanged: (active: boolean) => void;
  hasScreenConsent: () => Promise<boolean>;
  onConsentRevoked: (fn: () => void) => () => void;
  /** الهاتفُ المقترن — على ويندوز وحده. */
  phone?: {
    request: (cmd: PhoneCommand, args?: Record<string, unknown>) => Promise<PhoneReply>;
  };
}

declare global {
  interface Window {
    goyoneDesktop?: GoyoneDesktop;
  }
}

export const desktop: GoyoneDesktop | null =
  typeof window !== 'undefined' && window.goyoneDesktop?.isDesktop ? window.goyoneDesktop : null;
